import { Button, Dropdown, message } from 'antd';
import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import DangerButton from '../../shared/components/buttons/DangerButton';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import { PAGE_SIZE } from '../../shared/constants';
import AddCircleOutlineIcon from '../../shared/svgs/AddCircleOutlineIcon';
import { doDeleteCustomer, getCustomersList } from '../../api/customers';
import { CustomerI, CustomerSearchParamsT } from '../../interfaces/customer';
import CustomersFilterForm from '../../shared/components/forms/customer/Filter';
import CustomersTable from '../../shared/components/tables/Customers';
import { CurrentUserI } from '../../interfaces/auth';
import { handleAccessControl, handleFilterParams } from '../../utils/global';

const CustomersPage: React.FC = () => {
  const currentUser: CurrentUserI = useOutletContext();
  const access = handleAccessControl(currentUser.roles, 'customers');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const urlSearch = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryParams = useMemo(() => {
    const start = Number(urlSearch.get('start')) ?? 0;
    const count = Number(urlSearch.get('count')) !== 0 ? Number(urlSearch.get('count')) : PAGE_SIZE;
    const search = urlSearch.get('filter[name]');
    const orderBy = urlSearch.get('orderBy');
    const orderType = urlSearch.get('orderType');

    return {
      start,
      count,
      filter: {
        name: search,
      },
      orderBy,
      orderType,
    };
  }, [urlSearch]) as CustomerSearchParamsT;

  const { data, isLoading } = useQuery(['customers', 'list', queryParams], () => getCustomersList(queryParams), {
    cacheTime: 0,
  });

  const currentPage = queryParams.start ? Math.round((Number(queryParams.start) - 1) / PAGE_SIZE + 1) : 1;

  const pagination: any = {
    current: currentPage,
    pageSize: PAGE_SIZE,
    total: data?.total || 0,
    showTotal: (total: string) => `${total} Items`,
  };

  const setQueryParams = (params: any) => {
    const query = handleFilterParams(params);
    navigate(`?${query}`);
  };

  const handleFilter = (params: any) => {
    const currentPage = params.page;
    const offset = currentPage ? (currentPage === 1 ? 0 : (Number(currentPage) - 1) * PAGE_SIZE) : 0;

    const newQueryParams: CustomerSearchParamsT = { ...queryParams, start: offset, count: params.per_page };

    Object.keys(params).forEach((key: string) => {
      if (key.startsWith('sorts[')) {
        const fieldName = key.replace('sorts[', '').replace(']', '');
        const sortType = params[key].toUpperCase();
        newQueryParams.orderBy = fieldName as 'name';
        newQueryParams.orderType = sortType as 'ASC' | 'DESC';
      }
    });
    setQueryParams(newQueryParams);
  };

  const { mutateAsync: mutationDelete } = useMutation(doDeleteCustomer, {
    onSuccess: async () => {
      message.info('Deleted!');
      queryClient.invalidateQueries({
        queryKey: ['customers', 'list'],
      });
    },
  });

  const handleClickDelete = async (customer: CustomerI) => {
    mutationDelete(Number(customer.id));
  };

  const handleFilterSubmit = (values: any) => {
    setQueryParams({ ...queryParams, filter: values });
  };

  const handleResetFilter = () => {
    setQueryParams({ start: 0, count: PAGE_SIZE, filter: undefined });
  };

  const withAction = (record: any) => {
    return (
      <span className="action" style={{ display: 'flex' }}>
        <Dropdown.Button
          overlayClassName="table-action-menu"
          menu={{
            items: [
              access.isCanRead
                ? {
                    key: '1',
                    label: (
                      <Link to={`/customers/${record?.id}`}>
                        <Button className="btn-table-action-primary" type="link">
                          View
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanUpdate
                ? {
                    key: '2',
                    label: (
                      <Link to={`/customers/${record?.id}/edit`}>
                        <Button className="btn-table-action-primary" type="link">
                          Edit
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanDelete
                ? {
                    key: '3',
                    label: <DangerButton label="Delete" onOk={() => handleClickDelete(record)} />,
                  }
                : null,
            ],
          }}
        />
      </span>
    );
  };

  return (
    <div className="customers-container">
      <div className="page-container">
        <div className="page-header">
          <div></div>
          <div className="page-header-actions">
            {access.isCanCreate && (
              <Link to="/customers/new">
                <PrimaryButton label="Create Customer" icon={<AddCircleOutlineIcon />} />
              </Link>
            )}
          </div>
        </div>
        <div className="page-content">
          <CustomersFilterForm
            handleFilter={handleFilterSubmit}
            handleReset={handleResetFilter}
            queryParams={queryParams}
          />
          <CustomersTable
            rowKey="id"
            loading={isLoading}
            pagination={pagination}
            dataSource={data?.data}
            withAction={withAction}
            onChange2={handleFilter}
            query={queryParams}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;
